import { Box, ButtonBase, IconButton, Paper, Stack, styled, Typography } from '@mui/material';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { type WorkflowStepField, WorkflowStepFieldKind } from 'src/gql/graphql';
import {
  type WorkflowDefinitionNode,
  WorkflowNodeType,
} from 'src/pages/Settings/workflows/utils/workflow-definition';
import { ancestorIds, focusPath } from 'src/pages/Settings/workflows/utils/workflow-folding';
import { formatNodeTitle } from 'src/pages/Settings/workflows/utils/workflow-helpers';
import {
  readableTemplate,
  summarizeStep,
} from 'src/pages/Settings/workflows/utils/workflow-step-summary';
import { useWorkflowEditor } from 'src/pages/Settings/workflows/workflow-editor-context';

const DRAWER_WIDTH = 360;

const Drawer = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  width: `min(${DRAWER_WIDTH}px, 100%)`,
  zIndex: 6,
  display: 'flex',
  flexDirection: 'column',
  borderLeft: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
  borderRadius: 0,
  boxShadow: theme.shadows[8],
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
}));

const CheckRow = styled(ButtonBase)(({ theme }) => ({
  width: '100%',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  padding: theme.spacing(0.75, 1),
  borderRadius: theme.shape.borderRadius,
  textAlign: 'left',
  '&:hover': { backgroundColor: theme.palette.action.hover },
}));

/** A filled field as a person reads it: on/off, an option's name, a template's names, a count. */
const fieldText = (field: WorkflowStepField, value: unknown): string | null => {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  if (field.kind === WorkflowStepFieldKind.Enum) {
    return field.options?.find((option) => option.value === value)?.label ?? String(value);
  }
  if (typeof value === 'string') return readableTemplate(value);
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    return value.length ? `${value.length} item${value.length === 1 ? '' : 's'}` : null;
  }
  if (typeof value === 'object') {
    const keys = Object.keys(value);
    if (!keys.length) return null;
    return field.kind === WorkflowStepFieldKind.KeyValue ? keys.join(', ') : 'Set';
  }
  return null;
};

/**
 * Focus's panel, as in the Workflows prototype: "Path to <step>". It says whether the step can be
 * reached, lists each check on the way in order — ✓ when the way goes through its True output, ✗
 * through False — and ends with what the step itself does. Only what leads to the step, not what
 * follows it. Pressing a check focuses that check; Close or Escape ends Focus.
 */
export const WorkflowFocusDrawer = () => {
  const { definition, catalog, problemsByNode, view } = useWorkflowEditor();
  const { focusedId, focusStep, clearFocus } = view;
  if (!focusedId) return null;

  const byId = new Map(definition.nodes.map((node) => [node.id, node]));
  const focused = byId.get(focusedId);
  if (!focused) return null;

  const title = formatNodeTitle(focused.type, catalog);
  const summaryOf = (node: WorkflowDefinitionNode) =>
    summarizeStep(node, catalog.byType.get(node.type), problemsByNode.get(node.id) ?? []);

  const triggerId = definition.nodes.find(({ type }) => type === WorkflowNodeType.Trigger)?.id;
  const isTrigger = focused.type === WorkflowNodeType.Trigger;
  const reachable =
    isTrigger || (!!triggerId && ancestorIds(definition, focusedId).has(triggerId));

  // each `if` on the way, with the answer that leads on toward the step
  const path = focusPath(definition, focusedId);
  const checks = path.flatMap(({ nodeId }, index) => {
    const node = byId.get(nodeId);
    const via = path[index + 1]?.via;
    return node?.type === WorkflowNodeType.If && typeof via === 'boolean'
      ? [{ node, passes: via }]
      : [];
  });

  let status: string;
  if (isTrigger) status = 'This is where the workflow starts.';
  else if (!reachable) status = 'Can’t be reached: nothing from the trigger leads here.';
  else if (!checks.length) status = 'Runs every time — no checks on the way here.';
  else {
    status = `Runs when ${checks.length === 1 ? 'this check answers' : 'these checks answer, in order,'} as shown:`;
  }

  const focusedSummary = summaryOf(focused);
  // the settings that are filled, apart from one the summary line already says
  const fields = (catalog.byType.get(focused.type)?.fields ?? []).flatMap((field) => {
    if (field.reference) return [];
    const text = fieldText(field, focused.data?.[field.key]);
    return text && text !== focusedSummary ? [{ label: field.label, text }] : [];
  });

  return (
    <Drawer
      className="nodrag nopan nowheel"
      role="complementary"
      aria-label={`Path to ${title}`}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
      >
        <DynamicIcon name="center_focus_strong" fontSize="small" color="primary" />
        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1, minWidth: 0 }} noWrap>
          Path to {title}
        </Typography>
        <IconButton size="small" aria-label="Close focus" onClick={clearFocus}>
          <DynamicIcon name="close" fontSize="small" />
        </IconButton>
      </Stack>
      <Stack spacing={2} sx={{ p: 2, overflow: 'auto' }}>
        <Stack spacing={0.5}>
          <Typography variant="body2" color={reachable ? 'text.primary' : 'error'}>
            {status}
          </Typography>
          {checks.map(({ node, passes }) => (
            <CheckRow key={node.id} onClick={() => focusStep(node.id)}>
              <DynamicIcon
                name={passes ? 'check' : 'close'}
                fontSize="small"
                aria-hidden
                sx={{ color: passes ? 'success.main' : 'error.main', mt: '2px' }}
              />
              <Box minWidth={0}>
                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                  {summaryOf(node)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {passes ? 'True' : 'False'}
                </Typography>
              </Box>
            </CheckRow>
          ))}
        </Stack>
        {isTrigger ? null : (
          <Stack spacing={0.75}>
            <SectionTitle>
              {focused.type === WorkflowNodeType.If ? 'Then it checks' : 'Then it does'}
            </SectionTitle>
            <Typography variant="body2" fontWeight={600}>
              {title}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ overflowWrap: 'anywhere' }}
            >
              {focusedSummary}
            </Typography>
            {fields.length ? (
              <Box
                component="dl"
                sx={{
                  m: 0,
                  display: 'grid',
                  gridTemplateColumns: 'max-content minmax(0, 1fr)',
                  columnGap: 1.5,
                  rowGap: 0.5,
                }}
              >
                {fields.map(({ label, text }) => (
                  <Box key={label} sx={{ display: 'contents' }}>
                    <Typography component="dt" variant="caption" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography
                      component="dd"
                      variant="caption"
                      sx={{ m: 0, overflowWrap: 'anywhere' }}
                    >
                      {text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : null}
          </Stack>
        )}
      </Stack>
    </Drawer>
  );
};
